import { inject, Injectable } from '@angular/core';
import moment, { isDate } from 'moment';
import 'moment-timezone';
import 'moment/min/locales';
import { LanguageService } from '../../core/services/language.service';
import { UserConfigurationService } from '../../core/services/user-configuration.service';
import { OspDateTime } from '../contract/osp-date-time.contract';
import { TimezoneFormatLocaleDate } from '../contract/timezone-format-local-date.contract';

const DEFAULT_DATE_TIME_FORMAT: TimezoneFormatLocaleDate = {
  dateFormat: 'L',
  showDay: true,
  showTime: false,
  showTimeZone: false,
  hideEndOfDayTime: false,
}

@Injectable({ providedIn: 'root' })
export class DatesService {
  private languageService = inject(LanguageService);
  private userConfigurationService = inject(UserConfigurationService);

  static format = 'YYYY-MM-DD';
  static zonedDateTimeFormat = 'YYYY-MM-DDTHH:mm:ssZ';
  static utcFormat = 'YYYY-MM-DDTHH:mm:ss[Z]';
  private readonly defaultConfig = DEFAULT_DATE_TIME_FORMAT;

  static date(date: string | Date, format?: string): string | null {
    if (date) {
      format = format ? format : this.format;
      return this.isValidDate(date) ? moment(date).format(format) : null;
    }
    return null;
  }

  dateFormatLocale(date: OspDateTime | Date | string | undefined | null, config: TimezoneFormatLocaleDate = {}): string | null {
    const timestamp = (<OspDateTime>date)?.timestamp || <string | Date>date;
    if (!DatesService.isValidDate(timestamp)) {
      return null;
    }

    const {
      dateFormat,
      showDay,
      showTime,
      showTimeZone,
      hideEndOfDayTime
    } = { ...this.defaultConfig, ...(config || {}) };
    const converted = moment(timestamp).locale(this.languageService.getCurrentLocale());

    let dayPart = undefined;
    let timePart = undefined;
    let timeZonePart = undefined;
    if (showDay) {
      dayPart = dateFormat ? converted.format(dateFormat) : '';
    }
    if (showTime) {
      timePart = !(hideEndOfDayTime && DatesService.isEndOfDaySameTimeZone(timestamp)) ? converted.format('LT') : '';
    }
    if (showTimeZone) {
      const timeZone = converted
        .tz(this.userConfigurationService.getUserTimezone())
        .format('z');
      timeZonePart = `(${timeZone})`;
    }

    return [dayPart, timePart, timeZonePart].filter(value => !!value).join(' ');
  }

  static isEndOfDaySameTimeZone(date: Date | string): boolean {
    const currentDate = new Date(date);
    return isDate(currentDate) && currentDate.getHours() === 23 && currentDate.getMinutes() === 59;
  }

  static getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  static ospDateTime(date: string | Date): OspDateTime | null {
    const instant = this.isValidDate(date) ? moment(date).utc().format(this.utcFormat) : null;
    if (instant) {
      return {
        timestamp: instant,
        zoneId: this.getLocalTimeZone(),
      };
    }
    return null;
  }

  static ospDateTimeAtStartOfDay(date: string | Date): OspDateTime | null {
    const instant = this.isValidDate(date) ? moment(date).startOf('day').utc().format(this.utcFormat) : null;
    if (instant) {
      return {
        timestamp: instant,
        zoneId: this.getLocalTimeZone(),
      };
    }
    return null;
  }

  static ospDateTimeAtEndOfDay(date: string | Date): OspDateTime | null {
    const instant = (date && moment(date).isValid() && moment(date).isAfter('1900-01-01') && moment(date).isBefore('9999-12-31'))
      ? moment(date).endOf('day').utc().format(this.utcFormat) : null;
    if (instant) {
      return {
        timestamp: instant,
        zoneId: this.getLocalTimeZone(),
      };
    }
    return null;
  }

  static isValidDate(value: OspDateTime | Date | string | undefined | null): boolean {
    if (!value) {
      return false;
    }

    const date = (typeof value === 'object' && 'timestamp' in value)
      ? value.timestamp
      : value;

    try {
      if (typeof date === 'string') {
        const parsed = moment(date, moment.ISO_8601, true);
        return parsed.isValid() && parsed.isAfter('1900-01-01') && parsed.isBefore('9999-12-31');
      }
      const m = moment(date);
      return m.isValid() && m.isAfter('1900-01-01') && m.isBefore('9999-12-31');
    } catch {
      return false;
    }
  }

  static startOfDayIso(date: Date | string): string {
    return moment(date).startOf('day').toISOString();
  }

  static endOfDayIso(date: Date | string): string {
    return moment(date).endOf('day').toISOString();
  }

  static sameTimeZoneAtStartDateUTC(date: string | Date): string | null {
    if (date) {
      const utcDate = moment.utc(moment(date).startOf('day'));
      return this.isValidDate(date) ? utcDate.format(this.zonedDateTimeFormat) : null;
    }
    return null;
  }

  static sameTimeZoneAtEndDateUTC(date: string | Date): string | null {
    if (date) {
      const utcDate = moment.utc(moment(date).endOf('day'));
      return this.isValidDate(date) ? utcDate.format(this.zonedDateTimeFormat) : null;
    }
    return null;
  }
}
